import React, { useEffect, useState } from "react";
import { MotionConfig, motion } from "framer-motion";
import Login from "./components/Login";
import SkeletonPage from "./components/SkeletonPage";
import { api } from "./apiClient";
import { brandReady } from "./brandAssets";
import { uiMotion } from "./uiStyles";
import { LOGIN_STORAGE_KEY, REMEMBER_ME_STORAGE_KEY, SETTINGS_STORAGE_KEY, TOKEN_STORAGE_KEY } from "./config";

const SESSION_KEYS = [LOGIN_STORAGE_KEY, TOKEN_STORAGE_KEY, SETTINGS_STORAGE_KEY, REMEMBER_ME_STORAGE_KEY];
const sessionChannel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("tracker-session") : null;

// A session kept without "remember me" lives in sessionStorage, which a new tab (a link from an email)
// does not inherit. Ask the tabs that are already open to hand it over before showing the login form.
function borrowSessionFromOtherTabs() {
  const hasOwn = (localStorage.getItem(LOGIN_STORAGE_KEY) || sessionStorage.getItem(LOGIN_STORAGE_KEY)) === "true";
  if (hasOwn || !sessionChannel) return Promise.resolve();
  return new Promise((resolve) => {
    const timer = window.setTimeout(done, 400);
    function done() {
      window.clearTimeout(timer);
      sessionChannel.removeEventListener("message", onMessage);
      resolve();
    }
    function onMessage(event) {
      if (event.data?.type !== "session" || !event.data.values?.[TOKEN_STORAGE_KEY]) return;
      Object.entries(event.data.values).forEach(([key, value]) => value != null && sessionStorage.setItem(key, value));
      done();
    }
    sessionChannel.addEventListener("message", onMessage);
    sessionChannel.postMessage({ type: "request" });
  });
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [session, setSession] = useState(null);
  const [restoring, setRestoring] = useState(true);
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const invitationMatch = pathname.match(/^\/invite\/([^/]+)\/?$/);

  useEffect(() => {
    if (!sessionChannel) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === "request" && sessionStorage.getItem(LOGIN_STORAGE_KEY) === "true") {
        sessionChannel.postMessage({
          type: "session",
          values: Object.fromEntries(SESSION_KEYS.map((key) => [key, sessionStorage.getItem(key)])),
        });
      }
      if (event.data?.type === "signout") clearSession();
    };
    sessionChannel.addEventListener("message", onMessage);
    return () => sessionChannel.removeEventListener("message", onMessage);
  }, []);

  useEffect(() => {
    // The blank restoring phase also waits for the logo font and icons, so the first paint is complete.
    Promise.all([borrowSessionFromOtherTabs(), brandReady()]).then(restoreSession);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function restoreSession() {
    const saved = localStorage.getItem(LOGIN_STORAGE_KEY) || sessionStorage.getItem(LOGIN_STORAGE_KEY);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) || sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const rawSettings = localStorage.getItem(SETTINGS_STORAGE_KEY) || sessionStorage.getItem(SETTINGS_STORAGE_KEY);

    if (saved === "true" && token) {
      setIsLoggedIn(true);
      if (rawSettings) {
        try {
          const parsedSettings = JSON.parse(rawSettings);
          setSession({
            email: "",
            role: "executor",
            isAdmin: false,
            settings: parsedSettings,
            avatarUrl: parsedSettings?.avatarUrl || "",
          });
        } catch {
          localStorage.removeItem(SETTINGS_STORAGE_KEY);
          sessionStorage.removeItem(SETTINGS_STORAGE_KEY);
        }
      }

      api.get("/me")
        .then((response) => {
          setSession({
            ...(response.data || {}),
            avatarUrl: response.data?.settings?.avatarUrl || response.data?.avatarUrl || "",
            yourIp: response.data?.yourIp || "",
          });
          const useLocal = localStorage.getItem(REMEMBER_ME_STORAGE_KEY) === "true";
          const storage = useLocal ? localStorage : sessionStorage;
          storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(response.data.settings));
        })
        .catch(() => {
          [localStorage, sessionStorage].forEach((storage) => {
            storage.removeItem(LOGIN_STORAGE_KEY);
            storage.removeItem(TOKEN_STORAGE_KEY);
            storage.removeItem(SETTINGS_STORAGE_KEY);
            storage.removeItem(REMEMBER_ME_STORAGE_KEY);
          });
          setIsLoggedIn(false);
          setSession(null);
        });
    }
    setRestoring(false);
  }

  const handleLogin = async (payload) => {
    const useLocal = !!payload?.rememberMe;
    const primary = useLocal ? localStorage : sessionStorage;
    const secondary = useLocal ? sessionStorage : localStorage;

    primary.setItem(LOGIN_STORAGE_KEY, "true");
    primary.setItem(TOKEN_STORAGE_KEY, payload.token);
    primary.setItem(REMEMBER_ME_STORAGE_KEY, String(useLocal));
    if (payload?.settings) {
      primary.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(payload.settings));
    }

    secondary.removeItem(LOGIN_STORAGE_KEY);
    secondary.removeItem(TOKEN_STORAGE_KEY);
    secondary.removeItem(SETTINGS_STORAGE_KEY);
    secondary.removeItem(REMEMBER_ME_STORAGE_KEY);

    setSession({
      ...(payload || {}),
      settings: payload?.settings || {},
      avatarUrl: payload?.settings?.avatarUrl || payload?.avatarUrl || "",
      yourIp: payload?.yourIp || "",
    });
    setIsLoggedIn(true);
    try {
      const response = await api.get("/me");
      setSession({
        ...(response.data || payload || {}),
        settings: response.data?.settings || payload?.settings || {},
        avatarUrl: response.data?.settings?.avatarUrl || response.data?.avatarUrl || payload?.settings?.avatarUrl || payload?.avatarUrl || "",
        yourIp: response.data?.yourIp || payload?.yourIp || "",
      });
      if (response.data?.settings) {
        primary.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(response.data.settings));
      }
    } catch {
      setSession((prev) => prev || payload || null);
    }
  };

  function clearSession() {
    [localStorage, sessionStorage].forEach((storage) => {
      storage.removeItem(LOGIN_STORAGE_KEY);
      storage.removeItem(TOKEN_STORAGE_KEY);
      storage.removeItem(SETTINGS_STORAGE_KEY);
      storage.removeItem(REMEMBER_ME_STORAGE_KEY);
    });
    setIsLoggedIn(false);
    setSession(null);
  }

  const handleSignOut = () => {
    clearSession();
    sessionChannel?.postMessage({ type: "signout" });
  };

  const handleSessionUpdate = (payload) => {
    setSession((prev) => ({ ...(prev || {}), ...(payload || {}) }));
    if (payload?.settings) {
      const useLocal = localStorage.getItem(REMEMBER_ME_STORAGE_KEY) === "true";
      const storage = useLocal ? localStorage : sessionStorage;
      storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(payload.settings));
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      {restoring ? null : isLoggedIn
        ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.45, ease: uiMotion.ease }}>
            <SkeletonPage onSignOut={handleSignOut} session={session} onSessionUpdate={handleSessionUpdate} />
          </motion.div>
        )
        : <Login onLogin={handleLogin} invitationToken={invitationMatch ? decodeURIComponent(invitationMatch[1]) : ""} />}
    </MotionConfig>
  );
}

export default App;
